/**
 * Os produtos do projeto e a publicação: é o que põe o projeto no site.
 *
 * O site mostra o pinout gráfico de um componente quando o produto do projeto
 * está `publicado`, com `publicado_data` até hoje, **e** com `publicado_pinout`
 * (e o componente ligado a um sistema — a aba Sistemas). Sem `publicado_pinout`
 * o produto mostra só os diagramas publicados. É a regra do `DadoDB` que o site
 * usa, conferida em 28/09/2026.
 *
 * **A data vai sempre junto.** O `PUT` grava `publicado_data` do corpo, e sem
 * ela grava 01/01/1900: a tela que só mandava as duas marcas apagava a data de
 * publicação a cada clique. O legado mandava a data do formulário toda vez.
 * Aqui: mantém a que já existe e, ao publicar um produto que nunca teve data,
 * usa a de hoje.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

export type ProdutoDoProjeto = {
  projetoId: number
  produtoId: number
  publicado: boolean
  publicadoData: string | null
  publicadoPinout: boolean
  produtoNome: string
}

export type MarcasDoProduto = { produtoId: number; publicado: boolean; publicadoPinout: boolean }

/** 01/01/1900 (ou 0001) é o "sem data" do banco. */
export function temData(d: string | null | undefined): d is string {
  return !!d && d.slice(0, 4) !== "1900" && d.slice(0, 4) !== "0001"
}

/** A data a gravar: a que já existe; se não há e está publicando, hoje. */
export function dataParaGravar(atual: string | null | undefined, publicado: boolean, hoje = new Date()): string | null {
  if (temData(atual)) return atual.slice(0, 10)
  if (!publicado) return null
  const d = new Date(hoje.getTime() - hoje.getTimezoneOffset() * 60_000)
  return d.toISOString().slice(0, 10)
}

/** No ar para o cliente, com pinout gráfico. */
export function pinoutNoAr(p: ProdutoDoProjeto, hoje = new Date()): boolean {
  return p.publicado && p.publicadoPinout && (!temData(p.publicadoData) || new Date(p.publicadoData) <= hoje)
}

export const chaveDosProdutos = (projetoId: number) => ["projeto-produtos", projetoId] as const

export function usarProdutosDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: chaveDosProdutos(projetoId),
    enabled: projetoId > 0,
    queryFn: () => obter<ProdutoDoProjeto[]>(`api/projetos/${projetoId}/produtos`, {}),
  })
}

export function usarSalvarProduto(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (m: MarcasDoProduto) => {
      const atual = qc.getQueryData<ProdutoDoProjeto[]>(chaveDosProdutos(projetoId))
        ?.find((p) => p.produtoId === m.produtoId)
      await api.put(`api/projetos/${projetoId}/produtos`, {
        ...m,
        publicadoData: dataParaGravar(atual?.publicadoData, m.publicado),
      }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chaveDosProdutos(projetoId) }),
  })
}
